/**
 * Format date based on Kotlin getFormattedDate()
 * Converts server time to local timezone
 */
export const formatDate = (dateString: string) => {
  try {
    const serverDate = new Date(dateString);
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const formatter = new Intl.DateTimeFormat("ar-SA-u-ca-gregory", {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: userTimezone,
    });

    const parts = formatter.formatToParts(serverDate);
    const day = parts.find((p) => p.type === "day")?.value || "";
    const month = parts.find((p) => p.type === "month")?.value || "";
    const year = parts.find((p) => p.type === "year")?.value || "";

    // Convert Arabic numerals to English for day and year
    const toEnglishDigits = (str: string) =>
      str.replace(/[\u0660-\u0669]/g, (d) =>
        "٠١٢٣٤٥٦٧٨٩".indexOf(d).toString()
      );

    return `${toEnglishDigits(day)} ${month} ${toEnglishDigits(year)}`;
  } catch (error) {
    console.error("Error formatting date:", error, dateString);
    return dateString;
  }
};

/**
 * Format time based on Kotlin getFormattedTime()
 * Converts server time to local timezone
 */
export const formatTime = (dateString: string) => {
  try {
    const serverDate = new Date(dateString);
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const formatter = new Intl.DateTimeFormat("ar-SA-u-ca-gregory", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: userTimezone,
    });

    const parts = formatter.formatToParts(serverDate);
    const hour = parts.find((p) => p.type === "hour")?.value || "";
    const minute = parts.find((p) => p.type === "minute")?.value || "";
    const dayPeriod = parts.find((p) => p.type === "dayPeriod")?.value || "";

    // Convert Arabic numerals to English
    const toEnglishDigits = (str: string) =>
      str.replace(/[\u0660-\u0669]/g, (d) =>
        "٠١٢٣٤٥٦٧٨٩".indexOf(d).toString()
      );

    // Convert day period to Arabic text
    const convertDayPeriod = (period: string) => {
      if (period === "صباحاً" || period === "ص") {
        return "صباحاً";
      } else if (period === "مساءً" || period === "م") {
        return "مساءً";
      }
      return period;
    };

    return `${toEnglishDigits(hour)}:${toEnglishDigits(
      minute
    )} ${convertDayPeriod(dayPeriod)}`;
  } catch (error) {
    console.error("Error formatting time:", error, dateString);
    return dateString;
  }
};

// ─── Utility Functions ───────────────────────────────────────

/**
 * Formats currency amounts with proper decimal places and comma separators
 *
 * @param amount - The numeric amount to format
 * @returns Formatted string with proper currency formatting
 *
 * Examples:
 * - 0 → "0.00"
 * - 1234.56 → "1,234.56"
 * - 1000 → "1,000"
 */
export const formatAmount = (amount: number): string => {
  if (amount === 0) return "0.00";

  // Check if the amount has decimal places
  const hasDecimal = amount % 1 !== 0;

  if (hasDecimal) {
    // For decimal values, show exactly 2 decimal places with comma separators
    return Number(amount)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  } else {
    // For whole numbers, show as is with comma separators
    return Number(amount).toLocaleString();
  }
};
